import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Fab, CircularProgress, Button, Slide } from "@material-ui/core";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  CheckCircleOutlined,
  HighlightOff,
  Close,
  ReportProblemOutlined,
  PauseCircleOutline
} from "@material-ui/icons";
import { withAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import {
  getSmartMinutesDisplayTitle,
  getSmartMinutesJobLink,
  getSmartMinutesWhenLabel,
  isJobInProgress
} from "../utils/jobHelpers";
import JobsService from "../services/JobsService";

const FAB_QUEUE_LIMIT = 3;

const PENDING_MESSAGE_KEYS = {
  save_frequency: "pendingFrequency",
  save_occurrences: "pendingOccurrences",
  generate_class_report: "pendingReport",
  sync_lb: "pendingLbSync",
  process_smart_minutes: "pendingSmartMinutes",
  save_acl_profile: "pendingAclProfile",
  save_class_plan: "pendingClassPlan",
  save_class_stage: "pendingClassStage"
};

const SUCCESS_MESSAGE_KEYS = {
  save_frequency: "successFrequency",
  save_occurrences: "successOccurrences",
  generate_class_report: "successReport",
  sync_lb: "successLbSync",
  process_smart_minutes: "successSmartMinutes",
  save_acl_profile: "successAclProfile",
  save_class_plan: "successClassPlan",
  save_class_stage: "successClassStage"
};

const ERROR_MESSAGE_KEYS = {
  save_frequency: "errorFrequency",
  save_occurrences: "errorOccurrences",
  generate_class_report: "errorReport",
  sync_lb: "errorLbSync",
  process_smart_minutes: "errorSmartMinutes",
  save_acl_profile: "errorAclProfile",
  save_class_plan: "errorClassPlan",
  save_class_stage: "errorClassStage"
};

const getLocalizedJobStatusMessage = (job, t) => {
  if (!job) return "";
  if (isJobInProgress(job)) {
    if (job.status === "paused") return t("jobs.messages.paused");
    if (job.status === "retrying") return t("jobs.messages.retrying");
    const key = PENDING_MESSAGE_KEYS[job.name] || "pendingGrades";
    return t(`jobs.messages.${key}`);
  }
  if (job.status === "success") {
    if (job.error) return t("jobs.messages.partialError");
    const key = SUCCESS_MESSAGE_KEYS[job.name] || "successGrades";
    return t(`jobs.messages.${key}`);
  }
  if (job.status === "error") {
    const key = ERROR_MESSAGE_KEYS[job.name] || "errorGrades";
    return t(`jobs.messages.${key}`);
  }
  return "";
};

const getLocalizedJobAttemptsMessage = (job, t) => {
  const attempts = Number(job && job.attempts) || 0;
  if (attempts <= 0) return "";
  if (attempts === 1) return t("jobs.messages.attemptsOne");
  return t("jobs.messages.attemptsOther", { count: attempts });
};

const Container = styled.div`
  position: fixed;
  z-index: 9999;
  right: 16px;
  bottom: 16px;
  display: flex;
  align-items: ${({ open }) => (open ? "end" : "center")};
`;
const JobsButton = styled(Fab)`
  background-color: white;
  border-radius: 16px;
  width: 64px;
  height: 64px;
`;
const ArrowButton = styled(Button)`
  background-color: white;
  min-width: unset;
  padding: 0;
  width: 24px;
  height: 48px;
  margin-right: 4px;
  border-radius: 4px;
`;
const ArrowIcon = styled(ChevronLeft)`
  color: ${({ status }) => {
    if (status === "success") return "#5BC259";
    if (status === "error") return "#EE4848";
    if (status === "warning") return "#ffcc00";
    if (status === "paused") return "#E0A100";
    return "#1C1F74";
  }};
  width: 32px;
  transition: transform 0.2s linear;
  ${({ open }) =>
    open
      ? `
    transform: rotate(180deg);
  `
      : ""}
`;
const Loading = styled(CircularProgress)`
  color: #1c1f74;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;
const CompletedIcon = styled(CheckCircleOutlined)`
  color: #5bc259;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;
const ErrorIcon = styled(HighlightOff)`
  color: #ee4848;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;
const WarningIcon = styled(ReportProblemOutlined)`
  color: #ffcc00;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;
const PausedIcon = styled(PauseCircleOutline)`
  color: #e0a100;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`;
const Queue = styled.div`
  display: flex;
  flex-direction: column;
`;
const QueueItem = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 400px;
  padding: 15px;
  background-color: white;
  margin-right: 8px;
  margin-top: 4px;
  border-radius: 4px;
  box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2),
    0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12);
`;
const JobMessage = styled.p`
  margin: 0;
  font-family: Roboto, sans-serif;
  font-size: 18px;
  font-weight: 500;
`;
const MoreDetail = styled.span`
  color: #ee4848;
  text-decoration: underline;

  :hover {
    cursor: pointer;
    color: #c41414;
  }
`;
const JobDetail = styled.p`
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #828282;
  margin: 0;
`;
const JobAttempts = styled.p`
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #828282;
  margin: 2px 0 0;
`;
const CloseIcon = styled(Close)`
  color: #828282;
  width: 20px;
  height: 20px;
  :hover {
    cursor: pointer;
  }
`;
const JobIcons = styled.div`
  margin-left: 8px;
  display: flex;
  flex-direction: column;
  justify-content: ${({ canClose }) => (canClose ? "space-between" : "end")};
  align-items: center;
`;
const ViewMoreButton = styled(Button)`
  && {
    background-color: white;
    color: #1c1f74;
    margin-right: 8px;
    margin-top: 4px;
    border-radius: 4px;
    box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2),
      0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12);
    text-transform: none;
    font-weight: 500;

    &:hover {
      background-color: #f5f5f5;
    }
  }
`;
const DownloadLink = styled.button`
  margin-top: 6px;
  padding: 0;
  border: none;
  background: none;
  color: #1c1f74;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const AtaOpenRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
`;

const AtaOpenLink = styled(Link)`
  color: ${({ theme }) => theme.color.primary.dark};
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-decoration: underline;
  min-width: 0;

  &:hover {
    color: ${({ theme }) => theme.color.primary.main};
  }
`;

const AtaWhen = styled.span`
  flex: 0 0 auto;
  color: #828282;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
`;

const JobsQueue = ({ queue, markAsRead, alert }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  if (!queue.length) return null;

  const downloadReport = async job => {
    if (!job || !job.download || !job.download.available) return;
    setDownloadingId(job.id);
    try {
      const file = await JobsService.downloadJobFile(job.id);
      const fileName =
        (file && file.fileName) || job.download.fileName || "relatorio.pdf";
      const href = `data:application/pdf;base64,${file.base64}`;
      const link = document.createElement("a");
      link.href = href;
      link.download = fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error(e);
      alert.show(t("jobs.downloadError"), { title: t("common.actions.error") });
    } finally {
      setDownloadingId(null);
    }
  };

  let displayStatus;
  const hasSending = queue.some(
    queueItem =>
      queueItem.status === "pending" || queueItem.status === "retrying"
  );
  const hasPaused = queue.some(queueItem => queueItem.status === "paused");
  let failed = false;
  if (hasSending) {
    displayStatus = "pending";
  } else if (hasPaused) {
    displayStatus = "paused";
  } else {
    failed = queue.some(queueItem => queueItem.status === "error");
    if (failed) {
      displayStatus = "error";
    } else {
      const hasWarning = queue.some(
        queueItem => queueItem.status === "success" && queueItem.error
      );
      displayStatus = hasWarning ? "warning" : "success";
    }
  }

  let StatusIcon = null;
  if (displayStatus === "pending") StatusIcon = Loading;
  if (displayStatus === "paused") StatusIcon = PausedIcon;
  if (displayStatus === "success") StatusIcon = CompletedIcon;
  if (displayStatus === "error") StatusIcon = ErrorIcon;
  if (displayStatus === "warning") StatusIcon = WarningIcon;

  const showAlert = errorMessage => {
    alert.show(errorMessage, { title: t("common.messages.errorOccurred") });
  };

  const visibleQueue = queue.slice(0, FAB_QUEUE_LIMIT);
  const hasMoreJobs = queue.length > FAB_QUEUE_LIMIT;

  return (
    <Container open={open}>
      <ArrowButton onClick={() => setOpen(!open)} variant="contained">
        <ArrowIcon open={open} status={displayStatus} />
      </ArrowButton>
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Queue>
          {visibleQueue.map(job => {
            let statusText = null;
            let ItemStatusIcon = null;
            const attemptsMessage = getLocalizedJobAttemptsMessage(job, t);
            if (job.status === "paused") {
              statusText = (
                <div>
                  <JobMessage>
                    {getLocalizedJobStatusMessage(job, t)}
                  </JobMessage>
                  {attemptsMessage && (
                    <JobAttempts>{attemptsMessage}</JobAttempts>
                  )}
                </div>
              );
              ItemStatusIcon = PausedIcon;
            } else if (isJobInProgress(job)) {
              statusText = (
                <div>
                  <JobMessage>
                    {getLocalizedJobStatusMessage(job, t)}
                  </JobMessage>
                  {attemptsMessage && (
                    <JobAttempts>{attemptsMessage}</JobAttempts>
                  )}
                </div>
              );
              ItemStatusIcon = Loading;
            }
            if (job.status === "success") {
              if (job.error) {
                statusText = (
                  <div>
                    <JobMessage>
                      {t("jobs.finishedWithErrors")}
                      {" "}
                      <MoreDetail onClick={() => showAlert(job.error)}>
                        {t("jobs.someErrors")}
                      </MoreDetail>
                    </JobMessage>
                    {attemptsMessage && (
                      <JobAttempts>{attemptsMessage}</JobAttempts>
                    )}
                  </div>
                );
                ItemStatusIcon = WarningIcon;
              } else {
                statusText = (
                  <JobMessage>
                    {getLocalizedJobStatusMessage(job, t)}
                  </JobMessage>
                );
                ItemStatusIcon = CompletedIcon;
              }
            }
            if (job.status === "error") {
              statusText = (
                <div>
                  <JobMessage>
                    {getLocalizedJobStatusMessage(job, t)}
                    {" "}
                    <MoreDetail onClick={() => showAlert(job.error)}>
                      {t("jobs.moreDetails")}
                    </MoreDetail>
                  </JobMessage>
                  {attemptsMessage && (
                    <JobAttempts>{attemptsMessage}</JobAttempts>
                  )}
                </div>
              );
              ItemStatusIcon = ErrorIcon;
            }

            const canClose = !isJobInProgress(job);

            const canDownload =
              job.status === "success" &&
              job.download &&
              job.download.available;
            const ataLink =
              job.status === "success" ? getSmartMinutesJobLink(job) : null;
            const ataTitleRaw = getSmartMinutesDisplayTitle(job);
            const ataTitle =
              ataTitleRaw === "Ata Inteligente"
                ? t("jobs.type.smartMinutes")
                : ataTitleRaw;
            const ataWhen = ataLink ? getSmartMinutesWhenLabel(job) : null;

            return (
              <QueueItem key={job.id}>
                <div>
                  {ataTitle ? (
                    <>
                      <JobMessage>{ataTitle}</JobMessage>
                      <JobDetail>
                        {job.status === "success" && !job.error
                          ? t("jobs.ataSuccess")
                          : getLocalizedJobStatusMessage(job, t)}
                        {job.classId
                          ? ` ${t("jobs.classSuffix", { id: job.classId })}`
                          : ""}
                      </JobDetail>
                      {attemptsMessage &&
                        (isJobInProgress(job) || job.status === "error") && (
                          <JobAttempts>{attemptsMessage}</JobAttempts>
                        )}
                    </>
                  ) : (
                    <>
                      <JobDetail>{job.title}</JobDetail>
                      {statusText}
                    </>
                  )}
                  {ataLink && (
                    <AtaOpenRow>
                      <AtaOpenLink
                        to={ataLink}
                        onClick={() => {
                          setOpen(false);
                          if (canClose) markAsRead(job.id);
                        }}
                      >
                        {t("jobs.openAta")}
                      </AtaOpenLink>
                      {ataWhen && <AtaWhen>{ataWhen}</AtaWhen>}
                    </AtaOpenRow>
                  )}
                  {canDownload && (
                    <DownloadLink
                      type="button"
                      disabled={downloadingId === job.id}
                      onClick={() => downloadReport(job)}
                    >
                      {downloadingId === job.id
                        ? t("jobs.downloading")
                        : t("jobs.downloadPdf")}
                    </DownloadLink>
                  )}
                </div>
                <JobIcons canClose={canClose}>
                  {canClose && <CloseIcon onClick={() => markAsRead(job.id)} />}
                  <ItemStatusIcon size={24} />
                </JobIcons>
              </QueueItem>
            );
          })}
          {(hasMoreJobs || queue.length > 0) && (
            <ViewMoreButton
              component={Link}
              to="/solicitacoes"
              variant="contained"
              onClick={() => setOpen(false)}
            >
              {t("jobs.viewMore")}
            </ViewMoreButton>
          )}
        </Queue>
      </Slide>
      <JobsButton onClick={() => setOpen(!open)}>
        <StatusIcon size={48} />
      </JobsButton>
    </Container>
  );
};

JobsQueue.propTypes = {
  queue: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      status: PropTypes.string,
      error: PropTypes.string,
      name: PropTypes.string,
      title: PropTypes.string,
      download: PropTypes.shape({
        available: PropTypes.bool,
        fileName: PropTypes.string
      })
    })
  ).isRequired,
  markAsRead: PropTypes.func.isRequired,
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired
};

export default withAlert()(JobsQueue);
